import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Building2, Smartphone, Home as HomeIcon, CalendarDays, Play, ArrowRight, ThumbsUp, RefreshCw, TrendingUp, Check, Star, Quote } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { pillars, testimonials, trustedLogos, resources, stats, features, g2Badges } from '../data/mock';
import { useConfig } from '../context/ConfigContext';

const chipIcons = { Building2, Smartphone, Home: HomeIcon, CalendarDays };
const pillarIcons = { ThumbsUp, RefreshCw, TrendingUp };

const Hero = () => {
  const { contact } = useConfig();
  return (
  <section className="pp-hero-bg relative overflow-hidden">
    <div className="absolute inset-0 pp-dot-grid opacity-40" />
    <div className="absolute -right-32 top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full pp-wave opacity-80" />
    <div className="absolute right-32 top-1/2 -translate-y-1/2 w-28 h-28 rounded-full bg-[#F59E0B] flex items-center justify-center shadow-2xl hover:scale-105 transition-transform cursor-pointer">
      <Play className="w-10 h-10 text-[#0d2818] ml-1" fill="#0d2818" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-32">
      <div className="max-w-2xl pp-fade-up">
        <p className="text-white/90 text-xl md:text-2xl font-semibold tracking-tight mb-4">Family-Owned Pest Control · Serving Southern California</p>
        <h1 className="text-white text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.05]">
          Real Results, <span className="pp-italic-display text-[#8BC53F]">Forget the Rest!</span>
        </h1>
        <div className="mt-6 text-white text-2xl md:text-4xl font-extrabold tracking-tight">
          XPERTPESTS <span className="text-[#8BC53F]">SOLUTIONS</span>
        </div>
        <p className="text-[#8BC53F] mt-6 text-lg md:text-xl font-medium">📞 {contact.phone1} · {contact.phone2} · {contact.email}</p>

        <div className="mt-8 bg-white rounded-xl flex items-center p-2 shadow-2xl max-w-xl">
          <Input placeholder="Search for your Feature..." className="border-0 focus-visible:ring-0 text-base h-12 flex-1" />
          <button className="w-12 h-12 rounded-lg bg-[#8BC53F] hover:bg-[#6FA82C] flex items-center justify-center text-white transition">
            <Search className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-3 max-w-xl">
          {[{n:'Commercial',i:'Building2'},{n:'Mobile App',i:'Smartphone'},{n:'Residential',i:'Home'},{n:'Scheduling',i:'CalendarDays'}].map(c => {
            const Ico = chipIcons[c.i];
            return (
              <Link key={c.n} to="/features" className="flex items-center gap-2 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/15 rounded-md text-white text-sm font-medium backdrop-blur transition">
                <Ico className="w-4 h-4" />{c.n}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
    <div className="relative -mb-1">
      <svg viewBox="0 0 1440 80" className="w-full block" preserveAspectRatio="none"><path d="M0,80 C480,0 960,0 1440,80 L1440,80 L0,80 Z" fill="white"/></svg>
    </div>
  </section>
  );
};

const BadgesRow = () => (
  <section className="bg-white py-10">
    <div className="max-w-6xl mx-auto px-6 flex flex-wrap justify-center gap-8 items-center">
      {g2Badges.map((b, i) => (
        <div key={i} className="text-center">
          <div className="relative w-[88px] h-[110px] mx-auto">
            <div className="absolute inset-x-0 top-0 bg-slate-900 text-white text-[8px] font-bold text-center py-1 tracking-wider">{b.season}</div>
            <div className="absolute inset-0 border-2 border-slate-900 flex flex-col items-center justify-center pt-6" style={{clipPath:'polygon(0 0,100% 0,100% 85%,50% 100%,0 85%)'}}>
              <div className="text-[8px] font-bold text-slate-700 px-1 text-center">{b.label.toUpperCase()}</div>
              <Star className="w-5 h-5 mt-1" fill={b.color === 'pink' ? '#F59E0B' : '#f59e0b'} stroke="none"/>
              <div className="text-[8px] font-extrabold text-slate-900 mt-1">G2</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const OutSell = () => (
  <section className="bg-white py-24">
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <p className="text-[#8BC53F] font-bold tracking-widest text-sm mb-3">THE COMPLETE PLATFORM</p>
        <h2 className="text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">Out-Sell Your Competition</h2>
        <p className="mt-6 text-slate-600 text-lg leading-relaxed">Built specifically for pest control operators, Xpertpests brings together the office, the field, and the customer in one seamless workflow. Whether you serve residential or commercial — we power your growth.</p>
        <div className="mt-8 grid grid-cols-2 gap-5">
          {stats.map(s => (
            <div key={s.label} className="border-l-4 border-[#F59E0B] pl-4">
              <div className="text-4xl font-extrabold text-slate-900">{s.num}</div>
              <div className="text-sm text-slate-600 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        <Button asChild className="mt-10 bg-[#8BC53F] hover:bg-[#6FA82C] text-white h-12 px-7 font-semibold">
          <Link to="/why-xpertpests">Learn More <ArrowRight className="w-4 h-4 ml-2"/></Link>
        </Button>
      </div>
      <div className="relative">
        <div className="absolute -inset-4 bg-gradient-to-br from-[#F59E0B]/30 to-[#8BC53F]/30 rounded-3xl blur-2xl"/>
        <img src="https://images.unsplash.com/photo-1670989292166-8b20b9530438?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="Technician with Xpertpests mobile app" className="relative rounded-2xl shadow-2xl object-cover w-full aspect-[5/4]"/>
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="bg-slate-50 py-24">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-[#8BC53F] font-bold tracking-widest text-sm mb-3">WHY XPERTPESTS</p>
        <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Operate. Automate. Accelerate.</h2>
        <p className="mt-4 text-slate-600 text-lg">The end-to-end solution for your pest control business.</p>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-7">
        {pillars.map(p => {
          const Ico = pillarIcons[p.icon];
          return (
            <Card key={p.title} className="pp-lift border-0 shadow-lg p-8 bg-white">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] flex items-center justify-center text-white mb-5">
                <Ico className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">{p.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{p.desc}</p>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

const FeatureGrid = () => (
  <section className="bg-white py-24">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-12">
        <p className="text-[#8BC53F] font-bold tracking-widest text-sm mb-3">ALL THE FEATURES</p>
        <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Everything You Need to be the Best in Pest</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.slice(0, 9).map(f => {
          const Ico = require('lucide-react')[f.icon] || Check;
          return (
            <div key={f.name} className="pp-lift group bg-white border border-slate-200 rounded-xl p-6 hover:border-[#8BC53F]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#8BC53F]/10 group-hover:bg-[#8BC53F] text-[#8BC53F] group-hover:text-white flex items-center justify-center transition">
                  <Ico className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-slate-900">{f.name}</h4>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.desc}</p>
                  <div className="flex gap-2 mt-3">
                    {f.residential && <span className="text-[10px] font-bold tracking-wider text-[#8BC53F] bg-[#8BC53F]/10 px-2 py-0.5 rounded">RESIDENTIAL</span>}
                    {f.commercial && <span className="text-[10px] font-bold tracking-wider text-[#6FA82C] bg-[#6FA82C]/10 px-2 py-0.5 rounded">COMMERCIAL</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-center mt-10">
        <Button asChild variant="outline" className="border-[#8BC53F] text-[#8BC53F] hover:bg-[#8BC53F] hover:text-white h-12 px-7 font-semibold">
          <Link to="/features">View All Features <ArrowRight className="w-4 h-4 ml-2"/></Link>
        </Button>
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="bg-[#0d2818] py-24 text-white relative overflow-hidden">
    <div className="absolute inset-0 pp-dot-grid opacity-30"/>
    <div className="relative max-w-7xl mx-auto px-6">
      <div className="text-center mb-14">
        <p className="text-[#F59E0B] font-bold tracking-widest text-sm mb-3">TRUSTED SOFTWARE</p>
        <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">The Trusted Software for Pest Professionals</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {testimonials.map(t => (
          <div key={t.name} className="pp-lift bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur">
            <Quote className="w-9 h-9 text-[#F59E0B] mb-4"/>
            <p className="text-white/90 leading-relaxed">“{t.quote}”</p>
            <div className="mt-6 flex items-center gap-3">
              <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover"/>
              <div>
                <div className="font-bold">{t.name}</div>
                <div className="text-sm text-white/60">{t.title}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const TrustedBy = () => (
  <section className="bg-white py-14 border-y border-slate-100">
    <div className="max-w-7xl mx-auto px-6">
      <p className="text-center text-sm font-bold tracking-[0.25em] text-slate-500 mb-8">TRUSTED BY</p>
      <div className="overflow-hidden">
        <div className="flex gap-14 pp-marquee w-max">
          {[...trustedLogos, ...trustedLogos].map((l, i) => (
            <div key={i} className="text-2xl font-extrabold text-slate-400 hover:text-slate-700 transition tracking-tight whitespace-nowrap">{l}</div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Resources = () => (
  <section className="bg-slate-50 py-24">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-14">
        <p className="text-[#8BC53F] font-bold tracking-widest text-sm mb-3">RESOURCES</p>
        <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Looking to Grow? We’ve Got You Covered.</h2>
        <p className="mt-4 text-slate-600 text-lg max-w-2xl mx-auto">Interested in learning how to grow your pest control business and better serve your customers?</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {resources.map(r => (
          <Card key={r.title} className="pp-lift overflow-hidden border-0 shadow-md bg-white">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={r.img} alt={r.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
            </div>
            <div className="p-6">
              <span className="text-[10px] font-bold tracking-widest text-[#6FA82C]">{r.type}</span>
              <h3 className="text-xl font-bold text-slate-900 mt-2 leading-snug">{r.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{r.desc}</p>
              <a href="#" className="mt-4 inline-flex items-center gap-1 text-[#8BC53F] font-semibold text-sm hover:gap-2 transition-all">{r.cta} <ArrowRight className="w-4 h-4"/></a>
            </div>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="relative py-24 overflow-hidden bg-gradient-to-br from-[#8BC53F] to-[#6FA82C]">
    <div className="absolute inset-0 pp-dot-grid opacity-30"/>
    <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
      <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">Ready to Become the Best in Pest?</h2>
      <p className="mt-4 text-lg text-white/90">Join 8,000+ pest control operators growing 55% faster with Xpertpests.</p>
      <div className="mt-8 flex flex-wrap gap-4 justify-center">
        <Button asChild className="bg-white text-[#8BC53F] hover:bg-slate-100 h-12 px-7 font-bold">
          <Link to="/pricing">Get Started Free</Link>
        </Button>
        <Button asChild variant="outline" className="border-white text-white bg-transparent hover:bg-white/10 h-12 px-7 font-semibold">
          <Link to="/login">Book a Demo</Link>
        </Button>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="bg-white">
      <Header />
      <Hero />
      <BadgesRow />
      <OutSell />
      <Pillars />
      <FeatureGrid />
      <Testimonials />
      <TrustedBy />
      <Resources />
      <CTA />
      <Footer />
    </div>
  );
}
